import React, { useState, useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useConfig } from '../../contexts/ConfigContext';
import { CMButton } from '../common/CMButton';
import DashboardSelector, { Dashboard } from './DashboardSelector';

const DashboardsContainer: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const config = useConfig();
  const theme = useTheme();

  // Create tools/dashboards array from config with backward compatibility
  const dashboards: Dashboard[] = useMemo(() => {
    try {
      // Prefer new toolsUrls naming
      if (config.toolsUrls && Array.isArray(config.toolsUrls)) {
        console.log('[DashboardsContainer] Using toolsUrls configuration');
        return config.toolsUrls.filter(tool => tool && tool.url); // Filter out invalid entries
      }
      
      // Fallback to dashboardUrls for backward compatibility
      if (config.dashboardUrls && Array.isArray(config.dashboardUrls)) {
        console.log('[DashboardsContainer] Using dashboardUrls configuration (backward compatibility)');
        return config.dashboardUrls.filter(dashboard => dashboard && dashboard.url);
      } 
      
      // Fallback to single dashboardUrl for backward compatibility
      if (config.dashboardUrl && typeof config.dashboardUrl === 'string') {
        console.log('[DashboardsContainer] Using single dashboardUrl configuration (backward compatibility)');
        return [{ name: 'Dashboard', url: config.dashboardUrl }];
      }
      
      console.log('[DashboardsContainer] No dashboard/tools configuration found');
      return [];
    } catch (error) {
      console.error('[DashboardsContainer] Error processing dashboard configuration:', error);
      return [];
    }
  }, [config]);

  // State for the currently selected dashboard
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(
    dashboards.length > 0 ? dashboards[0] : null
  );

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Reset loading state when dashboard changes
  const handleDashboardChange = (dashboard: Dashboard) => {
    setIsLoading(true);
    setHasError(false);
    setCurrentDashboard(dashboard);
  };

  if (dashboards.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" color="text.secondary" gutterBottom>
          No External Tools Available
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
          No external tools have been configured for this organisation yet. We can develop custom tools for your specific use cases - contact your administrator to discuss your requirements.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      height: 'calc(100vh - 66px)',
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: `${theme.palette.secondary.main}B3`,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ padding: 1 }}>
        <DashboardSelector 
          dashboards={dashboards} 
          currentDashboard={currentDashboard} 
          onDashboardChange={handleDashboardChange} 
        />
      </Box>
      
      <Box sx={{ 
        width: '100%',
        height: 'calc(100% - 48px)', // Height minus the tab selector height
        display: 'flex',
        justifyContent: 'center',
        padding: 2
      }}>
        {isLoading && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            zIndex: 2
          }}>
            <CircularProgress />
            <Typography color="white">Loading dashboard...</Typography>
          </Box>
        )}

        {hasError && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            padding: 3
          }}>
            <Typography variant="h6" color="error" gutterBottom>
              Failed to load dashboard
            </Typography>
            <Typography variant="body2" color="white">
              Please check your internet connection and try again.
            </Typography>
          </Box>
        )}

        {currentDashboard && (
          <Box 
            sx={{ 
              width: '100%', 
              maxWidth: '2000px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                width: '100%',
                position: 'relative',
                height: 0,
                paddingTop: 'calc(9/16 * 100%)', // 16:9 aspect ratio (9/16 = 0.5625)
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            >
              <iframe 
                title={`${currentDashboard.name} Dashboard`}
                src={currentDashboard.url}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: hasError ? 'none' : 'block',
                  backgroundColor: 'white',
                  borderRadius: '8px'
                }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                allowFullScreen
                sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DashboardsContainer; 